import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type Tariff } from '@/api/cargo';
import { PageHead, DataTable, Drawer, SectionCard, Field, StatusBadge, type Col } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { fmt } from '@/shared/format';
import { TRANSPORT_MODE, CHARGE_BASIS, toOptions } from '@/shared/cargo';

const empty = { Name: '', FKOriginWarehouseId: '', TransportMode: 0, Basis: 0, RatePerUnit: 0, MinCharge: 0, IsActive: true };

export function TariffsPage() {
  const toast = useToast();
  const [edit, setEdit] = useState<Tariff | null | undefined>(undefined);
  const [form, setForm] = useState({ ...empty });
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ['cargo', 'tariffs'], queryFn: cargoApi.tariffs });
  const warehousesQ = useQuery({ queryKey: ['cargo', 'warehouses'], queryFn: cargoApi.warehouses });

  function open(t: Tariff | null) {
    setEdit(t);
    setForm(t ? { Name: t.Name, FKOriginWarehouseId: t.FKOriginWarehouseId ?? '', TransportMode: t.TransportMode, Basis: t.Basis, RatePerUnit: t.RatePerUnit, MinCharge: t.MinCharge, IsActive: t.IsActive } : { ...empty });
  }
  async function save() {
    if (!form.Name.trim()) return toast('Нэр оруулна уу.', 'warning');
    setBusy(true);
    try {
      await cargoApi.saveTariff(edit ? edit.Id : null, { ...form, FKOriginWarehouseId: form.FKOriginWarehouseId || null });
      toast('Хадгаллаа.', 'success');
      setEdit(undefined);
      q.refetch();
    } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }

  const cols: Col<Tariff>[] = [
    { key: 'Name', label: 'Нэр', render: (r) => <button className="ct-link" onClick={(e) => { e.stopPropagation(); open(r); }}>{r.Name}</button> },
    { key: 'TransportMode', label: 'Тээвэр', render: (r) => r.TransportModeLabel },
    { key: 'Basis', label: 'Суурь', render: (r) => r.BasisLabel },
    { key: 'RatePerUnit', label: 'Нэгжийн үнэ', align: 'right', render: (r) => fmt(r.RatePerUnit) },
    { key: 'MinCharge', label: 'Доод төлбөр', align: 'right', render: (r) => fmt(r.MinCharge) },
    { key: 'IsActive', label: 'Төлөв', render: (r) => <StatusBadge label={r.IsActive ? 'Идэвхтэй' : 'Идэвхгүй'} color={r.IsActive ? 'var(--ts-ok)' : 'var(--ts-muted)'} /> },
  ];

  return (
    <div>
      <PageHead title="Тариф" subtitle="Жин/эзлэхүүн/тогтмол үнийн дүрэм."
        actions={<button className="ts-btn ts-btn-primary" onClick={() => open(null)}><i className="bi bi-plus-lg" /> Шинэ</button>} />
      <DataTable cols={cols} rows={q.data ?? []} onRowClick={open} empty="Тариф алга." />
      {edit !== undefined && (
        <Drawer title={edit ? edit.Name : 'Шинэ тариф'} open onClose={() => setEdit(undefined)}
          footer={<><button className="ts-btn ts-btn-ghost" onClick={() => setEdit(undefined)}>Болих</button><button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}><i className="bi bi-check2" /> Хадгалах</button></>}>
          <SectionCard icon="bi-cash-coin" title="Тарифын тохиргоо">
            <div className="ts-row">
              <div style={{ gridColumn: 'span 12' }}><Field label="Нэр" required><input className="ts-input" required value={form.Name} onChange={(e) => setForm({ ...form, Name: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 6' }}><Field label="Гарах агуулах" hint="Хоосон = бүх агуулахад"><select className="ts-select" value={form.FKOriginWarehouseId} onChange={(e) => setForm({ ...form, FKOriginWarehouseId: e.target.value })}><option value="">— бүгд —</option>{warehousesQ.data?.map((w) => <option key={w.Id} value={w.Id}>{w.Name}</option>)}</select></Field></div>
              <div style={{ gridColumn: 'span 3' }}><Field label="Тээвэр"><select className="ts-select" value={form.TransportMode} onChange={(e) => setForm({ ...form, TransportMode: Number(e.target.value) })}>{toOptions(TRANSPORT_MODE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field></div>
              <div style={{ gridColumn: 'span 3' }}><Field label="Суурь"><select className="ts-select" value={form.Basis} onChange={(e) => setForm({ ...form, Basis: Number(e.target.value) })}>{toOptions(CHARGE_BASIS).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field></div>
              <div style={{ gridColumn: 'span 6' }}><Field label="Нэгжийн үнэ (₮)"><input className="ts-input" type="number" value={form.RatePerUnit} onChange={(e) => setForm({ ...form, RatePerUnit: Number(e.target.value) })} /></Field></div>
              <div style={{ gridColumn: 'span 6' }}><Field label="Доод төлбөр (₮)"><input className="ts-input" type="number" value={form.MinCharge} onChange={(e) => setForm({ ...form, MinCharge: Number(e.target.value) })} /></Field></div>
              <div style={{ gridColumn: 'span 12' }}><label className="ts-flex-row" style={{ cursor: 'pointer' }}><input type="checkbox" checked={form.IsActive} onChange={(e) => setForm({ ...form, IsActive: e.target.checked })} /> Идэвхтэй</label></div>
            </div>
          </SectionCard>
        </Drawer>
      )}
    </div>
  );
}
